/**
 * Client HTTP de l'API.
 *
 * Le point important n'est pas d'appeler `fetch`, c'est de **classer les
 * échecs**. Chaque catégorie donne lieu à un comportement différent, et une seule
 * d'entre elles autorise à retirer quoi que ce soit de la file d'attente.
 */

import type { Op } from '@shared/ops.ts';
import type { SyncDelta } from '@shared/types.ts';

export type FailureReason =
  /** Pas de réseau, ou serveur injoignable. On réessaiera. */
  | 'offline'
  /** Token absent, invalide, ou secret ayant tourné. Il faut un nouveau lien. */
  | 'unauthorized'
  /** 429 : trop de requêtes. On attend, on ne perd rien. */
  | 'rate_limited'
  /** Quota quotidien du compte épuisé (D1 1027). On attend, on ne perd rien. */
  | 'quota_exhausted'
  /** Autre erreur serveur. On réessaiera. */
  | 'server';

export type ApiResult<T> =
  | { ok: true; value: T; rowsRead: number }
  | { ok: false; reason: FailureReason; retryAfter: number };

export interface PushResponse {
  applied: string[];
  skipped: string[];
  rejected: number[];
  cursor: number;
  delta: SyncDelta | null;
}

const BASE = '/api/v1';

/** GET /sync — delta depuis le curseur. Aucune écriture déclenchée. */
export async function fetchDelta(token: string, since: number, signal?: AbortSignal): Promise<ApiResult<SyncDelta>> {
  return request<SyncDelta>(`${BASE}/sync?since=${since}`, token, { method: 'GET', signal });
}

/**
 * POST /sync — pousse la file et récupère le delta dans la même réponse.
 *
 * Fournir `since` économise une requête par cycle d'écriture : sur le quota
 * quotidien, c'est autant de pris.
 */
export async function pushOps(
  token: string,
  ops: readonly Op[],
  since: number,
  signal?: AbortSignal,
): Promise<ApiResult<PushResponse>> {
  return request<PushResponse>(`${BASE}/sync?since=${since}`, token, {
    method: 'POST',
    body: JSON.stringify({ ops }),
    headers: { 'Content-Type': 'application/json' },
    signal,
  });
}

async function request<T>(url: string, token: string, init: RequestInit): Promise<ApiResult<T>> {
  let response: Response;

  try {
    response = await fetch(url, {
      ...init,
      headers: { ...init.headers, Authorization: `Bearer ${token}` },
      // L'API ne doit jamais être servie depuis un cache HTTP : un delta périmé
      // ferait reculer le curseur, et des lignes seraient manquées.
      cache: 'no-store',
    });
  } catch {
    // fetch ne rejette que sur une panne réseau — un 500 est une réponse.
    return { ok: false, reason: 'offline', retryAfter: 0 };
  }

  if (!response.ok) {
    const retryAfter = Number(response.headers.get('Retry-After') ?? 0) || 0;

    if (response.status === 401) return { ok: false, reason: 'unauthorized', retryAfter: 0 };
    if (response.status === 429) return { ok: false, reason: 'rate_limited', retryAfter: retryAfter || 60 };
    if (response.status === 503) return { ok: false, reason: 'quota_exhausted', retryAfter: retryAfter || 900 };

    return { ok: false, reason: 'server', retryAfter };
  }

  try {
    return {
      ok: true,
      value: (await response.json()) as T,
      rowsRead: Number(response.headers.get('X-D1-Rows-Read') ?? 0) || 0,
    };
  } catch {
    // Réponse 200 illisible : on la traite comme une panne, donc on conserve
    // la file et on réessaiera.
    return { ok: false, reason: 'server', retryAfter: 0 };
  }
}
